import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { Menu, X, Globe } from "lucide-react";

const Header = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onAdmin = location.pathname.startsWith("/admin");
  if (onAdmin) return null;

  const handleLogoClick = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      document.getElementById("hero-section")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const handleHashNav = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      // Wait for route to render then scroll
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    }
  };

  const navItems = [
    { to: "/#rates", label: t.nav.rates, hash: true, sectionId: "rates" },
    { to: "/#about", label: t.nav.about, hash: true, sectionId: "about" },
    { to: "/locatii", label: t.nav.locations },
    // { to: "/fidelitate", label: t.nav.loyalty },
  ];

  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-[#5a1a14]/10"
      data-testid="site-header"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a
          href="/"
          className="flex items-center gap-3 group"
          data-testid="logo-link"
          onClick={handleLogoClick}
        >
          <div className="flex flex-col leading-none">
            <div style={{ display: 'inline-block' }}>
              <span style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.05em', display: 'block' }} className="text-2xl md:text-3xl font-black text-burgundy uppercase">CO<span style={{ marginLeft: '-0.08em' }}>R</span>S<span style={{ marginLeft: '-0.04em' }}>A</span></span>
              <span style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '0.22em', display: 'block', textAlign: 'center' }} className="text-[8px] md:text-[9px] font-normal text-coral uppercase">Exchange</span>
            </div>
          </div>
          <span className="hidden md:inline text-[10px] uppercase tracking-overline text-[#5a1a14]/50">
            est. 1995
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {navItems.map((it) =>
            it.hash ? (
              <a
                key={it.to}
                href={it.to}
                onClick={(e) => handleHashNav(e, it.sectionId)}
                className="text-sm uppercase tracking-overline text-black/70 hover:text-coral transition-colors"
                data-testid={`nav-${it.label.toLowerCase()}`}
              >
                {it.label}
              </a>
            ) : (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-overline transition-colors ${
                    isActive ? "text-coral" : "text-black/70 hover:text-coral"
                  }`
                }
                data-testid={`nav-${it.label.toLowerCase()}`}
              >
                {it.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center border border-[#5a1a14]/20 overflow-hidden text-xs uppercase tracking-overline">
            <button
              type="button"
              onClick={() => setLang("ro")}
              className={`px-2.5 py-1.5 transition-colors ${lang === "ro" ? "bg-burgundy text-white" : "text-burgundy hover:text-coral"}`}
              aria-label="Romana"
            >
              RO
            </button>
            <span className="w-px h-4 bg-[#5a1a14]/20" />
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1.5 transition-colors ${lang === "en" ? "bg-burgundy text-white" : "text-burgundy hover:text-coral"}`}
              aria-label="English"
            >
              EN
            </button>
          </div>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-burgundy"
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#5a1a14]/10 bg-white" data-testid="mobile-nav">
          <div className="flex flex-col px-6 py-4 gap-3">
            {navItems.map((it) => (
              <a
                key={it.to}
                href={it.to}
                className="text-sm uppercase tracking-overline text-black/80 py-2"
                onClick={(e) => {
                  if (it.hash) {
                    handleHashNav(e, it.sectionId);
                  } else {
                    setOpen(false);
                  }
                }}
              >
                {it.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
